// What an agent remembers: the notes it saved with `remember`, oldest first (about 20 at most).
// Any member may delete one, and the agent's next run no longer has it. Reading the notes works
// while agents are switched off, and in a view-as session (which can't delete). Errors are the
// server's sentences, through agentErrorText; a passing failure to read them can be tried again.
// Focus stays inside the dialog while it is open, Escape closes it, and focus goes back to what
// opened it (useDialogFocus).
//
// Props: agentId; onClose; readOnly (a view-as session: the notes, no Delete, and a line that says why).
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { deleteNote, listNotes } from './agentsService';
import { agentErrorCanRetry, agentErrorText } from './agentErrors';
import useDialogFocus from './useDialogFocus';

// The characters the words use that aren't ASCII, so this file stays ASCII.
const ELLIPSIS = String.fromCharCode(0x2026);
const CROSS = String.fromCharCode(0x2715);

const SCRIM = 'fixed inset-0 z-[70] bg-black/40 dark:bg-black/60 flex items-center justify-center p-4';
const DIALOG = 'w-full max-w-md max-h-full overflow-y-auto rounded-xl bg-white dark:bg-[#0f0f16] border border-slate-200 dark:border-slate-800 p-4 space-y-3 shadow-2xl outline-none';
const TITLE = 'text-sm font-bold text-slate-900 dark:text-slate-100';
const CLOSE = 'text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300';
const QUIET = 'text-xs text-slate-500 dark:text-slate-400';
const DELETE = 'shrink-0 text-[11px] text-red-600 dark:text-red-400 hover:underline disabled:opacity-50';
const ERROR = 'flex items-center justify-between gap-2 text-[11px] text-red-600 dark:text-red-400';
const RETRY = 'shrink-0 rounded-md px-2.5 py-1 text-[11px] font-semibold text-red-700 dark:text-red-300 hover:bg-red-100 dark:hover:bg-red-500/10';

export default function NotesDialog({ agentId, onClose, readOnly = false }) {
  const [notes, setNotes] = useState(null); // null until they first come
  const [error, setError] = useState(null); // {text, retry}: what went wrong, and whether reading again may help
  const [deleting, setDeleting] = useState({}); // note id -> true while its delete waits
  const waiting = useRef(new Set());
  const dialog = useRef(null);

  const load = useCallback(async () => {
    try {
      const answer = await listNotes(agentId);
      setNotes(Array.isArray(answer?.notes) ? answer.notes : []);
      setError(null);
    } catch (err) {
      setError({ text: agentErrorText(err), retry: agentErrorCanRetry(err) });
    }
  }, [agentId]);
  useEffect(() => { load(); }, [load]);
  useDialogFocus(dialog, onClose);

  const remove = async (note) => {
    if (waiting.current.has(note.id)) return;
    waiting.current.add(note.id);
    setDeleting((now) => ({ ...now, [note.id]: true }));
    setError(null);
    let gone = false;
    try {
      await deleteNote(agentId, note.id);
      gone = true;
    } catch (err) {
      setError({ text: agentErrorText(err), retry: false });
    } finally {
      waiting.current.delete(note.id);
      setDeleting((now) => {
        const next = { ...now };
        delete next[note.id];
        return next;
      });
    }
    if (!gone) return;
    // Gone from the list at once, even should reading the list again fail.
    setNotes((now) => (now || []).filter((other) => other.id !== note.id));
    await load();
  };

  return (
    <div className={SCRIM}>
      <div ref={dialog} role="dialog" aria-modal="true" aria-label="Agent notes" tabIndex={-1} className={DIALOG}>
        <div className="flex items-center justify-between">
          <div className={TITLE}>What this agent remembers</div>
          <button type="button" onClick={onClose} className={CLOSE} aria-label="Close">{CROSS}</button>
        </div>
        {notes === null && !error && <div className={QUIET}>{`Loading${ELLIPSIS}`}</div>}
        {notes !== null && notes.length === 0 && <div className={QUIET}>No notes yet.</div>}
        {readOnly && notes !== null && notes.length > 0 && (
          <div className={QUIET}>{"You're viewing as someone else, so you can't delete notes."}</div>
        )}
        {notes !== null && notes.length > 0 && (
          <ul className="list-none m-0 p-0 space-y-1.5">
            {notes.map((note) => (
              <li key={note.id} className="flex items-start justify-between gap-2 text-xs text-slate-700 dark:text-slate-200">
                <span className="min-w-0 break-words whitespace-pre-wrap">{note.text}</span>
                {!readOnly && (
                  <button type="button" aria-label="Delete note" disabled={!!deleting[note.id]} onClick={() => remove(note)}
                          className={DELETE}>
                    Delete
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
        {error && (
          <div role="alert" className={ERROR}>
            <span>{error.text}</span>
            {error.retry && (
              <button type="button" onClick={() => { setError(null); load(); }} className={RETRY}>Try again</button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
